import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../app/hooks';
import { login } from '../../features/auth/authSlice';
import { sendMessage } from '../../features/chat/chatSlice';
import { setUiPayload } from '../../features/canvas/canvasSlice';

type AuthMode = 'login' | 'register';

export const Login = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [mode, setMode] = useState<AuthMode>('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const switchMode = () => {
    setMode((prevMode) => (prevMode === 'login' ? 'register' : 'login'));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      const credentials = mode === 'register' ? { name, email, password } : { email, password };
      await dispatch(login({ mode, credentials }));

      // Now that login is complete and state is updated, send the message
            dispatch(sendMessage('I have logged in successfully. What is the next step?'));
      dispatch(setUiPayload(null));
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || t('common.unexpectedError');
      setError(errorMessage);
      // Notify the AI that the login/registration failed
            dispatch(sendMessage(`INTERNAL_ACTION:AUTH_FAILED:${errorMessage}`));
    }
  };

  return (
    <div className="bg-gray-800 bg-opacity-60 backdrop-blur-md rounded-xl p-8 shadow-2xl w-full max-w-md border border-gray-700">
      <h2 className="text-3xl font-bold text-white text-center mb-2">
        {t(mode === 'login' ? 'login.welcome' : 'register.createAccount')}
      </h2>
      <p className="text-center text-gray-400 mb-8">
        {t(mode === 'login' ? 'login.signInContinue' : 'register.getStarted')}
      </p>

      {error && (
        <div className="bg-red-500/20 border border-red-500/30 text-red-300 text-sm rounded-lg p-3 mb-6 text-center">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {mode === 'register' && (
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1">
              {t('register.nameLabel')}
            </label>
            <input
              id="name"
              name="name"
              type="text"
              autoComplete="name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 block w-full bg-gray-900/50 border border-gray-700 rounded-md shadow-sm py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition"
            />
          </div>
        )}

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
            {t('login.emailLabel')}
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 block w-full bg-gray-900/50 border border-gray-700 rounded-md shadow-sm py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition"
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">
            {t('login.passwordLabel')}
          </label>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1 block w-full bg-gray-900/50 border border-gray-700 rounded-md shadow-sm py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition"
          />
        </div>

        <button
          type="submit"
          className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-indigo-500 transition-colors duration-200"
        >
          {t(mode === 'login' ? 'login.submitButton' : 'register.submitButton')}
        </button>
      </form>

      <div className="mt-6 text-center text-sm">
        <a href="#" onClick={switchMode} className="font-medium text-indigo-400 hover:text-indigo-300">
          {t(mode === 'login' ? 'login.switchToRegister' : 'register.switchToLogin')}
        </a>
      </div>
    </div>
  );
};
